<template>
  <div>
    <Snackbar :SnackBarComponent="SnackBarComponent" />
    <v-dialog v-model="signUpDialog" max-width="760" scrollable persistent>
      <v-card class="signup-dialog-card">
        <v-card-title class="signup-dialog-header">
          <div class="d-flex align-center justify-space-between w-100">
            <div>
              <div class="signup-title">Create Your Organization Account</div>
              <div class="signup-subtitle">
                Complete the details below to get started with XcelAudit.
              </div>
            </div>
            <v-btn
              icon
              variant="text"
              @click="
                signUpDialog = false;
                $refs.signUpForm && $refs.signUpForm.reset();
              "
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="signup-dialog-body">
          <v-form ref="signUpForm" lazy-validation>
            <!-- SECTION 1: Organization Details -->
            <div class="section-header mt-2">
              <v-icon size="18" color="primary" class="mr-2">mdi-domain</v-icon>
              <span>Organization Details</span>
            </div>
            <v-divider class="mb-4"></v-divider>

            <v-row density="compact">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="signUp.organisation_name"
                  label="Organization Name *"
                  variant="outlined"
                  density="compact"
                  class="premium-field"
                  :rules="[(v) => !!v || 'Required']"
                  prepend-inner-icon="mdi-office-building"
                ></v-text-field>
              </v-col>

                   <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="signUp.organisation_country"
                  :items="computedCountryList"
                  item-title="name"
                  item-value="name"
                  label="Country *"
                  variant="outlined"
                  density="compact"
                  class="premium-field"
                  :rules="[(v) => !!v || 'Required']"
                  prepend-inner-icon="mdi-earth"
                  @update:modelValue="onCountryChange"
                ></v-autocomplete>
              </v-col>

     
         
              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="signUp.organisation_state"
                  :items="computedStateList"
                  item-title="name"
                  item-value="name"
                  label="State *"
                  variant="outlined"
                  density="compact"
                  class="premium-field"
                  :rules="[(v) => !!v || 'Required']"
                  prepend-inner-icon="mdi-map"
                  :disabled="!signUp.organisation_country"
                  :no-data-text="
                    signUp.organisation_country
                      ? 'No states found'
                      : 'Select a country first'
                  "
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="signUp.organisation_zip_code"
                  label="ZIP / Postal Code *"
                  variant="outlined"
                  density="compact"
                  class="premium-field"
                  :rules="[(v) => !!v || 'Required']"
                  prepend-inner-icon="mdi-mailbox"
                ></v-text-field>
              </v-col>
   
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="signUp.gst_number"
                  label="GST Number *"
                  variant="outlined"
                  density="compact"
                  class="premium-field"
                  :rules="[
                    (v) => !!v || 'GST Number is required',
                    (v) =>
                      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                        v,
                      ) || 'Invalid GST format',
                  ]"
                  prepend-inner-icon="mdi-file-certificate"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- SECTION 2: Primary Contact -->
            <div class="section-header mt-4">
              <v-icon size="18" color="primary" class="mr-2"
                >mdi-account-tie</v-icon
              >
              <span>Primary Contact Person</span>
            </div>
            <v-divider class="mb-4"></v-divider>

            <v-row density="compact">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="signUp.organisation_person_name"
                  label="Contact Name *"
                  variant="outlined"
                  density="compact"
                  class="premium-field"
                  :rules="[(v) => !!v || 'Required']"
                  prepend-inner-icon="mdi-account"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="signUp.organisation_person_emailid"
                  label="Contact Email *"
                  variant="outlined"
                  density="compact"
                  class="premium-field"
                  :rules="[
                    (v) => !!v || 'Required',
                    (v) => /.+@.+\..+/.test(v) || 'Invalid email',
                  ]"
                  prepend-inner-icon="mdi-email"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-autocomplete
                  v-model="signUp.organisation_person_country_code"
                  :items="computedCountryCodeList"
                  item-title="label"
                  item-value="dialCode"
                  label="Code *"
                  variant="outlined"
                  density="compact"
                  class="premium-field"
                  :rules="[(v) => !!v || 'Required']"
                  prepend-inner-icon="mdi-phone"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="signUp.organisation_person_contact"
                  label="Mobile Number *"
                  variant="outlined"
                  density="compact"
                  class="premium-field"
                  :rules="[
                    (v) => !!v || 'Required',
                    (v) => /^\d{7,15}$/.test(v) || 'Invalid number',
                  ]"
                  prepend-inner-icon="mdi-cellphone"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="signup-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            color="primary"
            class="cancel-btn"
            @click="
              signUpDialog = false;
              $refs.signUpForm && $refs.signUpForm.reset();
            "
            >Cancel</v-btn
          >
          <v-btn
            variant="flat"
            color="primary"
            class="create-btn"
            :loading="signUploading"
            @click="validateFormMethod"
          >
            <v-icon start>mdi-check-circle</v-icon>
            Create Account
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-app-bar app density="compact" clipped-left color="white" elevation="1">
      <v-app-bar-title
        ><a href="https://xcelaudit.com">
          <v-img
            v-if="themeLogo"
            :src="themeLogo"
            contain
            height="70px"
            max-width="150px"
          ></v-img
        ></a>
      </v-app-bar-title>
      <v-spacer></v-spacer>
    </v-app-bar>

    <v-main>
      <v-card class="overflow-auto" elevation="0">
        <v-card-text>
          <v-row no-gutters>
            <v-col cols="12" xs="12" sm="12" md="12">
              <v-card elevation="0">
                <v-row no-gutters>
                  <v-col cols="12" xs="12" sm="12" md="8">
                    <v-card
                      :height="windowHeight"
                      color="primary"
                      elevation="0"
                    >
                      <v-container>
                        <v-row
                          align="center"
                          justify="center"
                          :style="{ height: windowHeight + 'px' }"
                        >
                          <v-col>
                            <div
                              align="center"
                              style="
                                font-size: 35px;
                                font-weight: bold;
                                line-height: 40px;
                              "
                            >
                              {{ current_header }}
                            </div>
                            <div align="center" class="my-6">
                              <v-img
                                max-width="180px"
                                v-if="
                                  current_header == 'Step by step work flow'
                                "
                                src="@/assets/organization.gif"
                              ></v-img>
                              <v-img
                                max-width="180px"
                                v-if="
                                  current_header ==
                                  'Step by step across all segments from pre planning till closure'
                                "
                                src="@/assets/checklist.gif"
                              ></v-img>
                              <v-img
                                max-width="180px"
                                v-if="
                                  current_header ==
                                  'Templates and work papers for structured work environment'
                                "
                                src="@/assets/worklist.gif"
                              ></v-img>
                              <v-img
                                max-width="180px"
                                v-if="
                                  current_header ==
                                  'Seemless integration of queries and resolutions'
                                "
                                src="@/assets/opinion.gif"
                              ></v-img>
                              <v-img
                                max-width="180px"
                                v-if="
                                  current_header ==
                                  'Real time updation of all legislative requirements.'
                                "
                                src="@/assets/question.gif"
                              ></v-img>
                              <div
                                class="text-white py-6"
                                style="font-size: 18px"
                              >
                                Make your audit more efficient and perfect and
                                in line with the requirements of all the
                                regulators
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card>
                  </v-col>
                  <v-col cols="12" xs="12" sm="12" md="4" align="center">
                    <v-card class="mt-10 py-8" max-width="400" elevation="0">
                      <v-card-title class="pb-4 pt-1 mt-0">
                        <v-row class="text-center">
                          <v-col cols="12" class="py-0" align="center">
                            <a href="https://xcelaudit.com">
                              <v-img
                                v-if="themeLogo"
                                :src="themeLogo"
                                contain
                                height="70px"
                                max-width="150px"
                              ></v-img
                            ></a>
                          </v-col>
                          <v-col cols="12" class="py-0">
                            <div
                              v-if="currentTitle != 'Change Temporary Password'"
                              class="mt-5"
                            >
                              <h3 class="dark_text-primary font-weight-regular">
                                {{ currentTitle }}
                              </h3>
                            </div>
                            <div v-else class="mt-5">
                              <h3 class="dark_text-primary font-weight-regular">
                                {{ currentTitle }}
                              </h3>
                            </div>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <v-window v-model="step" class="px-3">
                          <v-window-item :value="1">
                            <v-card-text>
                              <v-form ref="form" lazy-validation class="mt-2">
                                <v-text-field
                                  color="primary"
                                  variant="outlined"
                                  density="compact"
                                  class="field_height field_label_size field_font"
                                  label="Email ID *"
                                  :rules="[
                                    (v) => !!v || 'Email ID is required',
                                  ]"
                                  v-model="Login.EmailID"
                                >
                                  <template v-slot:prepend-inner>
                                    <v-icon color="primary">mdi-account</v-icon>
                                  </template>
                                </v-text-field>
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  label="Password *"
                                  color="primary"
                                  :type="isPwd ? 'text' : 'password'"
                                  @click:append-inner="isPwd = !isPwd"
                                  required
                                  class="field_height field_label_size field_font"
                                  :rules="[
                                    (v) => !!v || 'Password is required',
                                  ]"
                                  v-model="Login.Password"
                                >
                                  <template v-slot:prepend-inner>
                                    <v-icon color="primary">mdi-lock</v-icon>
                                  </template>
                                  <template v-slot:append-inner>
                                    <v-icon
                                      color="primary"
                                      @click="isPwd = !isPwd"
                                      >{{
                                        isPwd ? "mdi-eye" : "mdi-eye-off"
                                      }}</v-icon
                                    >
                                  </template>
                                </v-text-field>
                              </v-form>
                            </v-card-text>
                            <v-card-actions class="py-0 mt-n4">
                              <v-checkbox
                                density="compact"
                                class="my-0 py-0 ml-1 FontSize"
                                v-model="rememberme"
                                color="primary"
                                label="Remember Me"
                                :disabled="
                                  Login.EmailID === '' && Login.Password === ''
                                "
                              />
                              <v-spacer></v-spacer>
                              <v-btn
                                size="small"
                                text
                                class="FontSize mb-4"
                                color="primary text-capitalize fontStyle"
                                @click="step = 3"
                                >Forgot Password ?</v-btn
                              >
                            </v-card-actions>
                            <v-card-actions class="pt-0 pb-2">
                              <v-btn
                                block
                                variant="flat"
                                :loading="loading"
                                tile
                                color="secondary"
                                @click="ValidateSignUpForm()"
                                >Login</v-btn
                              >
                            </v-card-actions>
                            <div class="text-center">
                              <b
                                >Dont' have an account ?
                                <u
                                  class="text-primary cursorPointer mx-1"
                                  @click="signUpDialog = true"
                                  >Sign up</u
                                >
                              </b>
                            </div>
                          </v-window-item>
                          <v-window-item :value="2">
                            <v-card-text>
                              <v-form ref="formCP" lazy-validation>
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  color="primary"
                                  class="field_height field_label_size field_font"
                                  label="Enter New Password *"
                                  :type="isPwdNew ? 'text' : 'password'"
                                  @click:append-inner="isPwdNew = !isPwdNew"
                                  required
                                  :append-inner-icon="
                                    isPwdNew ? 'mdi-eye' : 'mdi-eye-off'
                                  "
                                  prepend-inner-icon="mdi-lock"
                                  :rules="[
                                    (v) => !!v || 'Password is required',
                                  ]"
                                  v-model="Password.NewPassword"
                                ></v-text-field>
                                <v-text-field
                                  class="field_height field_label_size field_font"
                                  variant="outlined"
                                  density="compact"
                                  label="Confirm New Password *"
                                  color="primary"
                                  :type="isPwdConfirm ? 'text' : 'password'"
                                  @click:append-inner="
                                    isPwdConfirm = !isPwdConfirm
                                  "
                                  required
                                  :append-inner-icon="
                                    isPwdConfirm ? 'mdi-eye' : 'mdi-eye-off'
                                  "
                                  prepend-inner-icon="mdi-lock"
                                  :rules="[
                                    (v) => !!v || 'Password is required',
                                  ]"
                                  v-model="Password.ConfirmPassword"
                                ></v-text-field>
                              </v-form>
                            </v-card-text>
                            <v-card-actions class="pt-0 pb-8 px-4">
                              <v-btn
                                block
                                variant="flat"
                                :loading="loading"
                                tile
                                color="secondary"
                                @click="ValidateConfirmPasswordForm()"
                                >Login</v-btn
                              >
                            </v-card-actions>
                          </v-window-item>
                          <v-window-item :value="3">
                            <v-card-text>
                              <v-form ref="formOTP">
                                <div class="text-center">
                                  Please Enter The Registered Email
                                </div>
                                <v-text-field
                                  density="compact"
                                  variant="outlined"
                                  label="Email"
                                  v-model="OTPEmail"
                                  color="primary"
                                  :rules="[
                                    (v) => !!v || 'Email ID is required',
                                  ]"
                                  class="mt-4 field_font field_label_size field_height"
                                ></v-text-field>
                                <div class="FontSize text-center">
                                  *Please enter the e-mail used for XcelAudit
                                  login/registration
                                </div>
                              </v-form>
                            </v-card-text>
                            <v-card-actions>
                              <v-btn
                                text
                                color="primary fontStyle"
                                size="small"
                                @click="step = 1"
                                >Back To Sign In</v-btn
                              >
                              <v-spacer></v-spacer>
                              <v-btn
                                tile
                                color="secondary"
                                size="small"
                                @click="ValidateOTP()"
                                >Next</v-btn
                              >
                            </v-card-actions>
                          </v-window-item>
                          <v-window-item :value="4">
                            <v-card-text>
                              <v-form ref="formConfirmOTP">
                                <div class="text-center">
                                  Enter the XcelAudit Activation Code
                                </div>
                                <v-text-field
                                  density="compact"
                                  variant="outlined"
                                  label="Code *"
                                  v-model="ConfirmOTP"
                                  color="primary"
                                  :rules="[(v) => !!v || 'Code is required']"
                                  class="mt-4 field_font field_label_size field_height"
                                ></v-text-field>
                                <v-text-field
                                  density="compact"
                                  variant="outlined"
                                  label="Enter New Password *"
                                  color="primary"
                                  v-model="ConfirmNewPassword"
                                  :rules="[
                                    (v) => !!v || 'Password is required',
                                  ]"
                                  class="field_height field_label_size field_font"
                                  :type="isPwdConfirmCode ? 'text' : 'password'"
                                  @click:append-inner="
                                    isPwdConfirmCode = !isPwdConfirmCode
                                  "
                                  required
                                  :append-inner-icon="
                                    isPwdConfirmCode ? 'mdi-eye' : 'mdi-eye-off'
                                  "
                                  prepend-inner-icon="mdi-lock"
                                ></v-text-field>
                                <v-text-field
                                  density="compact"
                                  variant="outlined"
                                  label="Re-enter New Password *"
                                  color="primary"
                                  v-model="ReenterNewPassword"
                                  :rules="[
                                    (v) => !!v || 'Password is required',
                                  ]"
                                  class="field_height field_label_size field_font"
                                  :type="isPwdConfirmCode ? 'text' : 'password'"
                                  @click:append-inner="
                                    isPwdConfirmCode = !isPwdConfirmCode
                                  "
                                  required
                                  :append-inner-icon="
                                    isPwdConfirmCode ? 'mdi-eye' : 'mdi-eye-off'
                                  "
                                  prepend-inner-icon="mdi-lock"
                                ></v-text-field>
                                <div class="FontSize text-center">
                                  Enter the XcelAudit Activation Code sent to
                                  your registered Email.
                                </div>
                              </v-form>
                            </v-card-text>
                            <v-card-actions>
                              <v-btn
                                text
                                color="primary"
                                size="small"
                                @click="step = 1"
                                >Back To Sign In</v-btn
                              >
                              <v-spacer></v-spacer>
                              <v-btn
                                text
                                color="secondary"
                                size="small"
                                @click="ValidateEnteredOTP()"
                                >Next</v-btn
                              >
                            </v-card-actions>
                          </v-window-item>
                        </v-window>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-main>
  </div>
</template>

<script>
import Snackbar from "@/components/Extras/Snackbar.vue";
import {
  signIn,
  signOut,
  resetPassword,
  confirmResetPassword,
  confirmSignIn,
} from "aws-amplify/auth";
import { GetThemeDetails } from "@/mixins/RESTAPIs/GetThemeDetails.js";
import { GetOrganizationLogo } from "@/mixins/Extras/GetOrganizationLogo.js";
import { Country, State } from "country-state-city";
import axios from "axios";
export default {
  data: () => ({
    themeLogo: "",
    isFormValid: false,
    step: 1,
    height: 0,
    windowHeight: 0,

    OTPEmail: "",
    ConfirmOTP: "",
    current_header: "",
    ConfirmNewPassword: "",
    ReenterNewPassword: "",

    Login: {
      EmailID: "",
      Password: "",
    },
    isPwd: false,
    loading: false,
    isPwdNew: false,
    rememberme: false,
    themeLoaded: false,
    isPwdConfirm: false,
    isPwdConfirmCode: false,
    domain: null,
    Password: {
      NewPassword: "",
      ConfirmPassword: "",
    },
    theme_details: {},
    SnackBarComponent: {},

    headerArrays: [
      "Step by step across all segments from pre planning till closure",
      "Templates and work papers for structured work environment",
      "Real time updation of all legislative requirements.",
      "Seemless integration of queries and resolutions",
      "Step by step work flow",
    ],
    signUpDialog: false,
    signUp: {
      organisation_name: "",
      organisation_adress: "",
      organisation_state: "",
      organisation_country: "",
      organisation_zip_code: "",
      gst_number: "",
      organisation_person_name: "",
      organisation_person_emailid: "",
      organisation_person_country_code: "",
      organisation_person_contact: "",
    },
    signUploading: false,
  }),
  components: {
    Snackbar,
  },
  mixins: [GetOrganizationLogo, GetThemeDetails],
  watch: {
    rememberme(val) {
      if (val == true) {
        localStorage.setItem("currentuseremail", JSON.stringify(this.Login));
      } else if (val == false) {
        delete localStorage.currentuseremail;
      }
    },
  },
  computed: {
    currentTitle() {
      switch (this.step) {
        case 1:
          return "Sign In";
        case 2:
          return "Change Temporary Password";
        case 3:
          return "Forgot Password ?";
        case 4:
          return "Forgot Password ?";
        default:
          return "Account created";
      }
    },
    computedCountryList() {
      return Country.getAllCountries().map((c) => ({
        name: c.name,
        isoCode: c.isoCode,
      }));
    },
    computedStateList() {
      if (!this.signUp.organisation_country) return [];
      // Look up isoCode from the stored country name
      const matched = Country.getAllCountries().find(
        (c) => c.name === this.signUp.organisation_country,
      );
      if (!matched) return [];
      return State.getStatesOfCountry(matched.isoCode).map((s) => ({
        name: s.name,
        isoCode: s.isoCode,
      }));
    },
    computedCountryCodeList() {
      const seen = new Set();
      return Country.getAllCountries()
        .filter((c) => {
          const code = c.phonecode.replace(/[^0-9]/g, "");
          if (!code || seen.has(code)) return false;
          seen.add(code);
          return true;
        })
        .map((c) => {
          const code = `+${c.phonecode.replace(/[^0-9]/g, "")}`;
          return {
            label: `${c.flag} ${code} (${c.isoCode})`,
            dialCode: code,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  async mounted() {
    this.themeLoaded = false;
    this.$store.commit("SET_THEME_LOADED", false);
    this.CheckRememberMe();
    this.windowHeight = window.innerHeight - 83;
    this.runHeaderMethod();
    let currentURL = window.location.href;
    let urlresult = this.extractCompanyName(currentURL);
    let result = await this.GetThemeDetailsMethod(urlresult);
    if (result.status == 200 && result.data.items) {
      let raw_theme_details = result.data.items[0];
      this.theme_details = {};
      for (let key1 in raw_theme_details) {
        for (let key2 in raw_theme_details[key1]) {
          this.theme_details[key1] = raw_theme_details[key1][key2];
        }
      }
      this.$store.commit("SET_THEME_DETAILS", this.theme_details);
      this.$store.commit("SET_THEME_LOADED", true);
      this.$vuetify.theme.themes.light.primary =
        this.theme_details.primary_theme_color_code.slice(0, -2);
      this.$vuetify.theme.themes.light.secondary =
        this.theme_details.secondary_theme_color_code.slice(0, -2);
      this.themeLoaded = true;
    }
    await this.GetOrganizationLogoMethod();
  },
  methods: {
    onCountryChange(countryName) {
      this.signUp.organisation_state = "";
      if (!countryName) {
        this.signUp.organisation_person_country_code = "";
        return;
      }
      const matched = Country.getAllCountries().find(
        (c) => c.name === countryName,
      );
      if (matched?.phonecode) {
        this.signUp.organisation_person_country_code = `+${matched.phonecode.replace(/[^0-9]/g, "")}`;
      }
    },
    async validateFormMethod() {
      console.log("Before signUp params", this.signUp);
      const isValid = await this.$refs.signUpForm.validate();
      if (isValid.valid) {
        await this.signUpWithRestApiMethod();
      } else {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "error",
          Top: true,
          SnackbarText: "Fields marked with asterisks (*) are mandatory",
        };
      }
    },

    getOrganisationCode() {
      return this.signUp.organisation_name.trim().slice(0, 3).toUpperCase();
    },

    async signUpWithRestApiMethod() {
      this.signUploading = true;
      try {
        // Strip Vue reactive proxy before sending
        const rawPayload = JSON.parse(
          JSON.stringify({
            organisation_name: this.signUp.organisation_name,
            organisation_code: this.getOrganisationCode(),
            organisation_state: this.signUp.organisation_state,
            organisation_country: this.signUp.organisation_country,
            organisation_zip_code: this.signUp.organisation_zip_code,
            organisation_data_center: "Mumbai",
            gst_number: this.signUp.gst_number,
            organisation_person_name: this.signUp.organisation_person_name,
            organisation_person_emailid:
              this.signUp.organisation_person_emailid,
            organisation_person_country_code:
              this.signUp.organisation_person_country_code,
            organisation_person_contact:
              this.signUp.organisation_person_contact,
          }),
        );

        console.log("rawPayload", rawPayload);  

        const apiUrl = process.env.VUE_APP_AWS_SIGNUP_REST_API_URL;
        let result = await axios.post(apiUrl, rawPayload, {
          headers: { "Content-Type": "application/json" },
        });

        console.log("result", result);
        if (result.status == 200) {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "Organization created successfully",
          };
          this.signUpDialog = false;
          this.$refs.signUpForm && this.$refs.signUpForm.reset();
        }
      } catch (error) {
        console.log("Full error response:", error);
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "error",
          Top: true,
          SnackbarText:
            error?.response?.data?.message ||
            error?.response?.data ||
            error.message,
        };
      } finally {
        this.signUploading = false;
      }
    },
    async ValidateSignUpForm() {
      let isValid = this.$refs.form.validate();
      try {
        const result = await isValid;
        if (result.valid) {
          await this.SignIn();
        } else {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "error",
            Top: true,
            SnackbarText: "Fields marked with asterisks (*) are mandatory",
          };
        }
      } catch (error) {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "error",
          Top: true,
          SnackbarText: error,
        };
      }
    },

    async SignIn() {
      try {
        this.loading = true;
        const { nextStep } = await signIn({
          username: this.Login.EmailID.toLowerCase().trim(),
          password: this.Login.Password.trim(),
        });
        if (
          nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          this.step = 2;
          this.loading = false;
          this.$store.commit("SET_USEREMAIL", this.Login.EmailID.toLowerCase());
        } else {
          this.$store.commit("SET_USEREMAIL", this.Login.EmailID.toLowerCase());
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "Login Successful",
          };
          this.ActivateMethod();
        }
      } catch (error) {
        this.loading = false;
        if (error.message === "There is already a signed in user.") {
          await this.logoutMethod(this.Login.EmailID.toLowerCase());
        } else {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "error",
            Top: true,
            SnackbarText: error.message,
          };
        }
      }
    },

    ActivateMethod() {
      this.loading = true;
      setTimeout(() => this.$router.push("/LandingPage"), 500);
      this.loading = false;
    },
    extractCompanyName(url) {
      const regex = /^https?:\/\/([^./]+)\./;
      const match = url.match(regex);
      if (match && match[1]) {
        return match[1];
      } else {
        return "DEFAULT";
      }
    },
    runHeaderMethod() {
      this.count = 0;
      this.current_header = this.headerArrays[this.count];
      setInterval(() => {
        if (this.count == this.headerArrays.length - 1) {
          this.count = 0;
        } else {
          this.count += 1;
        }
        this.current_header = this.headerArrays[this.count];
      }, 5000);
    },
    CheckRememberMe() {
      if (localStorage.getItem("currentuseremail") != null) {
        this.Login = JSON.parse(localStorage.getItem("currentuseremail"));
        this.rememberme = true;
      }
    },

    async ConfirmOTPMethod() {
      try {
        await confirmResetPassword({
          username: this.OTPEmail,
          confirmationCode: this.ConfirmOTP,
          newPassword: this.ConfirmNewPassword,
        }).then(() => {
          this.$refs.formConfirmOTP.reset();
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "Password Successfully Changed ",
          };
          this.step = 1;
        });
      } catch (err) {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "error",
          Top: true,
          SnackbarText: err.message,
        };
      }
    },
    async ValidateEnteredOTP() {
      let isValid = this.$refs.formConfirmOTP.validate();
      try {
        const result = await isValid;
        if (result.valid) {
          if (this.ReenterNewPassword == this.ConfirmNewPassword) {
            await this.ConfirmOTPMethod();
          } else {
            this.SnackBarComponent = {
              SnackbarVmodel: true,
              SnackbarColor: "error",
              Top: true,
              SnackbarText: "Password Does not match",
            };
          }
        } else {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "error",
            Top: true,
            SnackbarText: "Please Enter The Code Sent To Registered Email",
          };
        }
      } catch (error) {
        //console.log.error("error in ValidateEnteredOTP", error);
      }
    },

    async ValidateOTP() {
      let isValid = this.$refs.formOTP.validate();
      try {
        const result = await isValid;
        if (result.valid) {
          await this.SendOTPEmail();
        } else {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "error",
            Top: true,
            SnackbarText: "Please Enter Registered Email",
          };
        }
      } catch (error) {
        //console.log.error(error);
      }
    },
    async SendOTPEmail() {
      try {
        const output = await resetPassword({ username: this.OTPEmail });
        this.handleResetPasswordNextSteps(output);
      } catch (err) {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "error",
          Top: true,
          SnackbarText: err.message,
        };
      }
    },
    handleResetPasswordNextSteps(output) {
      const { nextStep } = output;
      switch (nextStep.resetPasswordStep) {
        case "CONFIRM_RESET_PASSWORD_WITH_CODE":
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "OTP Sent To Registered Email",
          };
          this.step = 4;
          break;
        case "DONE":
          break;
      }
    },

    async ConfirmPassword() {
      try {
        this.loading = true;
        const result = await confirmSignIn({
          challengeResponse: this.Password.ConfirmPassword,
        });
        if (result.nextStep.signInStep === "DONE") {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "Login Successful",
          };
          this.$refs.formCP.reset();
          this.ActivateMethod();
        }
        this.loading = false;
      } catch (err) {
        this.loading = false;
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "error",
          Top: true,
          SnackbarText: err.message,
        };
      }
    },
    async ValidateConfirmPasswordForm() {
      let isValid = this.$refs.formCP.validate();
      try {
        const result = await isValid;
        if (result.valid) {
          if (this.Password.NewPassword == this.Password.ConfirmPassword) {
            await this.ConfirmPassword();
          } else {
            this.$refs.form.reset();
            this.SnackBarComponent = {
              SnackbarVmodel: true,
              SnackbarColor: "error",
              Top: true,
              SnackbarText: "Password does not match",
            };
          }
        } else {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "error",
            Top: true,
            SnackbarText: "Fields marked with asterisks (*) are mandatory",
          };
        }
      } catch (error) {
        // //console.log.error("error in ValidateConfirmPasswordForm", error);
      }
    },

    async logoutMethod(Email) {
      try {
        const user = await signOut({ username: Email });
        this.ValidateSignUpForm();
        return user;
      } catch (error) {
        //console.log.error("Error signing in user", error);
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "error",
          Top: true,
          SnackbarText: error.message,
        };
        throw error;
      }
    },
  },
};
</script>

<style scoped>
.v-application .rounded-b1-x1 {
  border-bottom-left-radius: 300px !important;
}
.v-application .rounded-br-x1 {
  border-bottom-right-radius: 300px !important;
}
.signup-dialog-card {
  border-radius: 12px !important;
}
.signup-dialog-header {
  padding: 20px 24px 16px !important;
}
.signup-dialog-body {
  padding: 20px 24px !important;
}
.signup-dialog-actions {
  padding: 14px 24px !important;
}
.signup-title {
  font-size: 16px;
  font-weight: 700;
  color: #03376b;
  line-height: 1.3;
  margin-bottom: 3px;
}
.signup-subtitle {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.section-header {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #03376b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 8px;
}
.premium-field :deep(.v-field) {
  border-radius: 8px !important;
  font-size: 13px;
  transition: box-shadow 0.2s ease;
}
.premium-field :deep(.v-field:hover) {
  box-shadow: 0 0 0 2px rgba(3, 55, 107, 0.1);
}
.premium-field :deep(.v-field--focused) {
  box-shadow: 0 0 0 2px rgba(3, 55, 107, 0.2);
}
.premium-field :deep(.v-label) {
  font-size: 12.5px;
}
.cancel-btn {
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  border-color: #03376b !important;
  color: #03376b !important;
  min-width: 90px;
}
.create-btn {
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  background-color: #03376b !important;
  min-width: 150px;
  transition: opacity 0.2s ease;
}
.create-btn:hover {
  opacity: 0.88;
}
</style>
